import { ChevronDownIcon, XIcon } from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslations } from "../../i18n";
import {
  mergeRefs,
  textFieldButtonColor,
  textFieldButtonSize,
} from "../../utils";
import { clsx } from "clsx";
import { IconButton } from "../Button/IconButton";
import { useDropdownContext } from "../Dropdown";
import { Spinner } from "../Spinner";
import { type ISpinnerSize } from "../Spinner/types";
import { TruncatedContent } from "../TruncatedContent";
import { useSelectContext } from "./context";
import type { ISelectColor, ISelectSize, ISelectTriggerProps } from "./types";
import { FieldAccessory } from "../FieldAccessory";
import type { ITagSize, ITagColor } from "@/ui-kit";
import { Tag } from "@/ui-kit";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export const SelectTrigger = (props: ISelectTriggerProps = {}) => {
  const {
    color = "layer1",
    className,
    invalid,
    disabled,
    loading,
    placeholder,
    displayValue,
    showClearButton,
    onClear,
    onRenderAnchor,
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
    contentId,
    multiple,
    value,
    options,
    onSelect,
    ...contextProps
  } = useSelectContext();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: ISelectSize = contextProps.size || (isMobile ? "lg" : "md");
  const { t } = useTranslations();
  const { open } = useDropdownContext();
  const triggerRef = useRef<HTMLDivElement>(null);

  const rootClass = clsx(
    "nuco-select__trigger",
    `nuco-select__trigger--${size}`,
    `nuco-select__trigger--${color}`,
    disabled && "nuco-select__trigger--disabled",
    invalid && "nuco-select__trigger--invalid",
    open && "nuco-select__trigger--focused",
    className,
  );

  const triggerClass = onRenderAnchor ? className : rootClass;

  const fontSizeMap: Record<ISelectSize, string> = {
    sm: "nuco-typography--body",
    md: "nuco-typography--body",
    lg: "nuco-typography--body",
  };

  const spinnerSizeMap: Record<ISelectSize, ISpinnerSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const tagSize: Record<ISelectSize, ITagSize> = {
    sm: "sm",
    md: "md",
    lg: "lg",
  };

  const tagColor: Record<ISelectColor, ITagColor> = {
    layer1: "layer2",
    layer2: "layer3",
  };

  const selectedOptions = useMemo(() => {
    if (!value) {
      return [];
    }

    const valueArray = Array.isArray(value) ? value : [value];
    return options.filter((opt) => valueArray.includes(opt.id));
  }, [value, options]);

  const handleClearClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
      triggerRef.current?.focus();
    },
    [onClear],
  );

  const handleClearMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleClearKeyDown = useCallback((e: KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const dataAttributes = Object.fromEntries(
    Object.entries(contextProps).filter(([key]) => key.startsWith("data-")),
  );

  const placeholderJSX = (
    <span className="nuco-select__trigger-placeholder nuco-typography--body">
      {placeholder}
    </span>
  );

  return (
    <div
      {...props}
      {...dataAttributes}
      ref={mergeRefs(triggerRef, props.ref)}
      className={triggerClass}
      tabIndex={onRenderAnchor || disabled ? -1 : 0}
      role="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-busy={loading ? "true" : undefined}
      aria-disabled={disabled}
      aria-label={!ariaLabelledby ? ariaLabel : undefined}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
      aria-controls={contentId}
    >
      {onRenderAnchor ? (
        onRenderAnchor(open)
      ) : (
        <>
          <div className="nuco-select__trigger-inner">
            {multiple ? (
              selectedOptions.length > 0 ? (
                selectedOptions.map((option) => (
                  <Tag
                    key={option.id}
                    id={option.id}
                    text={option.text}
                    startIcon={option.icon}
                    color={tagColor[color]}
                    size={tagSize[size]}
                    onRemove={() => onSelect(option.id)}
                    disabled={disabled}
                  />
                ))
              ) : (
                placeholderJSX
              )
            ) : displayValue ? (
              <TruncatedContent
                className={clsx(
                  "nuco-select__trigger-value",
                  fontSizeMap[size],
                )}
                content={displayValue}
              />
            ) : (
              placeholderJSX
            )}
          </div>

          <div className="nuco-select__trigger-actions">
            {showClearButton && (
              <IconButton
                variant="filled"
                size={textFieldButtonSize[size]}
                color={textFieldButtonColor[color]}
                onClick={handleClearClick}
                onKeyDown={handleClearKeyDown}
                onMouseDown={handleClearMouseDown}
                onPointerDown={(e) => e.stopPropagation()}
                content={<XIcon />}
                aria-label={t("select.clearSelection")}
                tabIndex={0}
              />
            )}

            {!disabled && loading && (
              <FieldAccessory size={size}>
                <Spinner color="light" size={spinnerSizeMap[size]} />
              </FieldAccessory>
            )}
            {!disabled && !loading && (
              <IconButton
                variant="filled"
                size={textFieldButtonSize[size]}
                color={textFieldButtonColor[color]}
                tabIndex={-1}
              >
                <ChevronDownIcon
                  className={clsx(
                    "nuco-select__chevron",
                    open && "nuco-select__chevron--rotate",
                  )}
                />
              </IconButton>
            )}
          </div>
        </>
      )}
    </div>
  );
};
